import { DateRange } from "@win-reality/react-ui";
import { useCallback, useState } from "react";

function formatDateToString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseDateString(dateStr: string | undefined): Date | undefined {
  return dateStr ? new Date(dateStr + "T00:00:00") : undefined;
}

/**
 * Bridges string-based date state (e.g. from stores/URL params) with
 * react-day-picker's DateRange objects for use with DateRangePicker.
 *
 * Manages local DateRange state to avoid round-trip conversion issues,
 * and resets the range on single click when a complete range is already selected.
 */
export function useDateRange({
  initialFrom,
  initialTo,
  onFromChange,
  onToChange,
}: {
  initialFrom?: string;
  initialTo?: string;
  onFromChange: (date: string | undefined) => void;
  onToChange: (date: string | undefined) => void;
}) {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    if (!initialFrom && !initialTo) return undefined;
    return {
      from: parseDateString(initialFrom),
      to: parseDateString(initialTo),
    };
  });

  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      setDateRange((prev) => {
        // If previous range was complete, reset to new start date
        if (prev?.from && prev?.to && range?.from) {
          const reset = { from: range.from, to: undefined };
          onFromChange(formatDateToString(range.from));
          onToChange(undefined);
          return reset;
        }
        onFromChange(range?.from ? formatDateToString(range.from) : undefined);
        onToChange(range?.to ? formatDateToString(range.to) : undefined);
        return range;
      });
    },
    [onFromChange, onToChange],
  );

  return { dateRange, handleDateRangeChange };
}
